import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LogOut, Mountain, Inbox, ChevronDown, ChevronUp, MapPin, FileText, Loader2 } from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return "—"; }
}
function fmtEur(v) {
  const n = Number(v || 0);
  if (!n) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}
const EVENTO_LABELS = { terremoto: "Terremoto", inondazione: "Inondazione / Alluvione", franamento: "Frana / Smottamento" };
const PARTITA_LABELS = { fabbricato: "Fabbricato", macchinario: "Macchinario/attrezzature", merci: "Merci", terreni: "Terreni" };

const Field = ({ label, value }) => (
  <div className="text-sm">
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="font-medium text-forest">{value || "—"}</div>
  </div>
);

function RequestDetail({ r, onTrasforma, converting, onApri }) {
  const d = r.dati_cliente || {};
  const q = d.quotazione || {};
  const eventiAttivi = Object.entries(q.eventi || {}).filter(([, v]) => v).map(([k]) => EVENTO_LABELS[k] || k);
  const sin = d.sinistri;
  return (
    <div className="space-y-5 bg-sage/20 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-forest/70"><MapPin className="h-3.5 w-3.5" /> Ubicazione del rischio</p>
        {r.quote_id ? (
          <Button size="sm" data-testid={`catnat-apri-preventivo-${r.id}`} onClick={() => onApri(r.quote_id)}
            className="h-8 gap-1.5 bg-forest text-white hover:bg-forest/90"><FileText className="h-4 w-4" /> Apri preventivo</Button>
        ) : (
          <Button size="sm" data-testid={`catnat-trasforma-${r.id}`} disabled={converting} onClick={() => onTrasforma(r.id)}
            className="h-8 gap-1.5 bg-terracotta text-white hover:bg-terracotta-hover">
            {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mountain className="h-4 w-4" />} Trasforma in preventivo
          </Button>
        )}
      </div>
      <div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Indirizzo (via e civico)" value={q.indirizzo} />
          <Field label="Comune" value={q.comune} />
          <Field label="Provincia" value={q.provincia} />
          <Field label="CAP" value={q.cap} />
          <Field label="Zona sismica" value={q.zona_sismica} />
          <Field label="Classe alluvione" value={q.classe_alluvione} />
          <Field label="Data effetto" value={q.data_effetto} />
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-forest/70">Caratteristiche del fabbricato</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Materiale" value={q.materiale} />
          <Field label="Anno costruzione" value={q.anno_costruzione} />
          <Field label="Numero piani" value={q.numero_piani} />
          <Field label="Piano rischio" value={q.piano_rischio} />
          <Field label="Piani interrati" value={q.interrati ? "Sì" : "No"} />
          <Field label="Attività esercitata" value={q.attivita} />
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-forest/70">Partite assicurate</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Object.keys(PARTITA_LABELS).map((k) => <Field key={k} label={PARTITA_LABELS[k]} value={fmtEur((q.partite || {})[k])} />)}
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-forest/70">Garanzie e questionario</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Garanzie" value={eventiAttivi.join(", ")} />
          <Field label="Scoperto" value={q.scoperto != null ? `${q.scoperto}%` : "—"} />
          <Field label="Limite indennizzo" value={q.limite_indennizzo != null ? `${q.limite_indennizzo}%` : "—"} />
          <Field label="Sinistri dichiarati" value={sin ? (sin.ha_sinistri ? `Sì (${(sin.elenco || []).length})` : "No") : "—"} />
        </div>
      </div>
      {d.note && <Field label="Note del cliente" value={d.note} />}
    </div>
  );
}

export default function CatNatInbox() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [converting, setConverting] = useState(null);

  const load = () => api.get("/catnat/requests").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const apri = (qid) => navigate(`/catnat-inbox/preventivo/${qid}`);
  const trasforma = async (rid) => {
    setConverting(rid);
    try {
      const { data } = await api.post(`/catnat/requests/${rid}/trasforma`);
      toast.success("Lead trasformato in preventivo dell'Agenzia");
      navigate(`/catnat-inbox/preventivo/${data.quote_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setConverting(null); }
  };

  return (
    <div className="min-h-screen bg-bone" data-testid="catnat-inbox">
      <header className="sticky top-0 z-30 border-b border-border bg-bone/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 md:px-6">
          <div className="flex items-center gap-3">
            <Brand />
            <span className="hidden items-center gap-1.5 rounded-full bg-forest px-3 py-1 text-xs font-medium text-white sm:inline-flex">
              <Mountain className="h-3.5 w-3.5" /> CAT NAT
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground sm:inline">{user?.email}</span>
            <Button variant="ghost" size="icon" onClick={doLogout} data-testid="catnat-logout-button" title="Esci">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-4 py-8 md:px-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-forest">Richieste CAT NAT</h1>
            <p className="mt-1 text-muted-foreground">Richieste di proposta ricevute dalla landing page pubblica CAT NAT.</p>
          </div>
          <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-sm">
            <Inbox className="h-4 w-4" /> {items.length}
          </Badge>
        </div>

        <Card className="overflow-hidden rounded-xl border-border/60">
          <Table>
            <TableHeader><TableRow className="bg-sage/50">
              <TableHead>Impresa / ente</TableHead><TableHead>Tipologia</TableHead>
              <TableHead>Comune (Prov)</TableHead><TableHead>Contatti</TableHead>
              <TableHead>Stato</TableHead><TableHead>Data</TableHead><TableHead className="text-right">Dettagli</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Caricamento…</TableCell></TableRow>}
              {!loading && items.length === 0 &&
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Nessuna richiesta CAT NAT.</TableCell></TableRow>}
              {items.map((r) => {
                const q = r.dati_cliente?.quotazione || {};
                const open = openId === r.id;
                return (
                  <Fragment key={r.id}>
                    <TableRow data-testid={`catnat-request-${r.id}`} className="cursor-pointer" onClick={() => setOpenId(open ? null : r.id)}>
                      <TableCell className="font-medium text-forest">{r.dati_cliente?.ragione_sociale || "—"}</TableCell>
                      <TableCell className="text-sm">{r.cluster || "—"}</TableCell>
                      <TableCell className="text-sm">{q.comune ? `${q.comune}${q.provincia ? ` (${q.provincia})` : ""}` : "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {r.dati_cliente?.email || "—"}{r.dati_cliente?.telefono ? ` · ${r.dati_cliente.telefono}` : ""}
                      </TableCell>
                      <TableCell><StatusBadge state={r.stato} /></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" data-testid={`catnat-detail-toggle-${r.id}`} className="h-7 gap-1 text-forest">
                          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow key={`${r.id}-detail`} data-testid={`catnat-detail-${r.id}`}>
                        <TableCell colSpan={7} className="p-0"><RequestDetail r={r} onTrasforma={trasforma} converting={converting === r.id} onApri={apri} /></TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </main>
    </div>
  );
}
